'use client';

import { useDeferredValue, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  EditableImage,
  EditableText,
  sanitizeLinkUrl,
  sanitizeMediaUrl,
  sanitizeWhatsAppHref,
  trackEvent,
  useEditMode,
  type ImageValue,
  type SiteRendererProps,
} from '@locusly/client-runtime';
import englishHtml from '../source/index.html';
import workshopWebp from '../source/static/2025-03-16.webp';
import workshopJpg from '../source/static/IMG_8793-min-1024x763.jpg';
import logoPng from '../source/static/car-keepers-club-logo-full-e1678828338896-1024x566 (1).png';
import type {
  CarKeepersClubContent,
  FaqChipContent,
  FaqItemContent,
  LocalizedPageContent,
  CtaContent,
  DecisionContent,
  FooterContent,
  HeroContent,
  LocationContent,
  NavContent,
  PricingContent,
  SeoContent,
  ServiceCardContent,
  ServicesContent,
} from './content.schema';
import defaultContent from './defaultContent';
import portugueseHtml from '../source/pt/index.html';

type ImportedAsset = string | { src: string };
type Locale = 'en' | 'pt';
type MembershipPlan = 'fiveYear' | 'monthly';
type ResolvedContent = {
  brand: NonNullable<CarKeepersClubContent['brand']>;
  media: NonNullable<CarKeepersClubContent['media']>;
  en: LocalizedPageContent;
  pt: LocalizedPageContent;
};

const SITE_ROOT_ID = 'car-keepers-club-site';
const ANCHORS = {
  en: {
    services: '#services',
    about: '#about',
    pricing: '#pricing',
    location: '#location',
    faq: '#faq',
  },
  pt: {
    services: '#servicos',
    about: '#sobre',
    pricing: '#precos',
    location: '#localizacao',
    faq: '#perguntas',
  },
} satisfies Record<Locale, Record<'services' | 'about' | 'pricing' | 'location' | 'faq', string>>;

function assetUrl(asset: ImportedAsset) {
  return typeof asset === 'string' ? asset : asset.src;
}

const WORKSHOP_WEBP_URL = assetUrl(workshopWebp);
const WORKSHOP_JPG_URL = assetUrl(workshopJpg);
const LOGO_PNG_URL = assetUrl(logoPng);

function scopeStyles(styles: string) {
  return styles
    .replace(/\bbody::before\b/g, `#${SITE_ROOT_ID}::before`)
    .replace(/\bbody::after\b/g, `#${SITE_ROOT_ID}::after`)
    .replace(/\bbody(\s*[,{])/g, `#${SITE_ROOT_ID}$1`);
}

function extractStyles(source: string) {
  return Array.from(source.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi))
    .map((match) => match[1])
    .join('\n');
}

const pageStylesByLocale: Record<Locale, string> = {
  en: scopeStyles(extractStyles(englishHtml)),
  pt: scopeStyles(extractStyles(portugueseHtml)),
};

function safeHref(value: string | undefined, allowHash = false) {
  return (
    sanitizeLinkUrl(value, {
      allowHash,
      allowRelative: true,
    }) ?? '#'
  );
}

function normalizeServiceItems(
  current: ServiceCardContent[] | undefined,
  fallback: ServiceCardContent[],
) {
  return current?.length ? current : fallback;
}

function normalizeFaqChips(current: FaqChipContent[] | undefined, fallback: FaqChipContent[]) {
  return current?.length ? current : fallback;
}

function normalizeFaqItems(current: FaqItemContent[] | undefined, fallback: FaqItemContent[]) {
  return current?.length ? current : fallback;
}

function mergeLocaleContent(
  fallback: {
    seo: SeoContent;
    nav: NavContent;
    hero: HeroContent;
    services: ServicesContent;
    decision: DecisionContent;
    pricing: PricingContent;
    location: LocationContent;
    faq: {
      title: string;
      searchPlaceholder: string;
      emptyState: string;
      chips: FaqChipContent[];
      items: FaqItemContent[];
    };
    cta: CtaContent;
    footer: FooterContent;
  },
  current:
    | {
        seo?: Partial<SeoContent>;
        nav?: Partial<NavContent>;
        hero?: Partial<HeroContent>;
        services?: Partial<ServicesContent>;
        decision?: Partial<DecisionContent>;
        pricing?: Partial<PricingContent>;
        location?: Partial<LocationContent>;
        faq?: Partial<LocalizedPageContent['faq']>;
        cta?: Partial<CtaContent>;
        footer?: Partial<FooterContent>;
      }
    | undefined,
): LocalizedPageContent {
  return {
    seo: { ...fallback.seo, ...(current?.seo ?? {}) },
    nav: { ...fallback.nav, ...(current?.nav ?? {}) },
    hero: { ...fallback.hero, ...(current?.hero ?? {}) },
    services: {
      ...fallback.services,
      ...(current?.services ?? {}),
      items: normalizeServiceItems(current?.services?.items, fallback.services.items),
    },
    decision: { ...fallback.decision, ...(current?.decision ?? {}) },
    pricing: { ...fallback.pricing, ...(current?.pricing ?? {}) },
    location: { ...fallback.location, ...(current?.location ?? {}) },
    faq: {
      ...fallback.faq,
      ...(current?.faq ?? {}),
      chips: normalizeFaqChips(current?.faq?.chips, fallback.faq.chips),
      items: normalizeFaqItems(current?.faq?.items, fallback.faq.items),
    },
    cta: { ...fallback.cta, ...(current?.cta ?? {}) },
    footer: { ...fallback.footer, ...(current?.footer ?? {}) },
  };
}

function mergeContent(
  content: Record<string, Record<string, unknown>> | undefined,
): ResolvedContent {
  const incoming = (content ?? {}) as unknown as Partial<CarKeepersClubContent>;
  return {
    brand: { ...(defaultContent.brand ?? {}), ...(incoming.brand ?? {}) },
    media: { ...(defaultContent.media ?? {}), ...(incoming.media ?? {}) },
    en: mergeLocaleContent(
      {
        seo: defaultContent.enSeo,
        nav: defaultContent.enNav,
        hero: defaultContent.enHero,
        services: defaultContent.enServices,
        decision: defaultContent.enDecision,
        pricing: defaultContent.enPricing,
        location: defaultContent.enLocation,
        faq: defaultContent.enFaq,
        cta: defaultContent.enCta,
        footer: defaultContent.enFooter,
      },
      {
        seo: incoming.enSeo,
        nav: incoming.enNav,
        hero: incoming.enHero,
        services: incoming.enServices,
        decision: incoming.enDecision,
        pricing: incoming.enPricing,
        location: incoming.enLocation,
        faq: incoming.enFaq,
        cta: incoming.enCta,
        footer: incoming.enFooter,
      },
    ),
    pt: mergeLocaleContent(
      {
        seo: defaultContent.ptSeo,
        nav: defaultContent.ptNav,
        hero: defaultContent.ptHero,
        services: defaultContent.ptServices,
        decision: defaultContent.ptDecision,
        pricing: defaultContent.ptPricing,
        location: defaultContent.ptLocation,
        faq: defaultContent.ptFaq,
        cta: defaultContent.ptCta,
        footer: defaultContent.ptFooter,
      },
      {
        seo: incoming.ptSeo,
        nav: incoming.ptNav,
        hero: incoming.ptHero,
        services: incoming.ptServices,
        decision: incoming.ptDecision,
        pricing: incoming.ptPricing,
        location: incoming.ptLocation,
        faq: incoming.ptFaq,
        cta: incoming.ptCta,
        footer: incoming.ptFooter,
      },
    ),
  };
}

function preventEditNavigation(
  event: MouseEvent<HTMLElement>,
  editing: boolean,
) {
  if (editing) event.preventDefault();
}

function SectionRule() {
  return <span className="services-rule" aria-hidden="true" />;
}

function DecisionMatrixIcon({ index }: { index: number }) {
  return (
    <span className="matrix-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        {index === 0 ? (
          <>
            <path d="M8 3.5h6l4 4V20a1 1 0 0 1-1 1H8a2 2 0 0 1-2-2V5.5a2 2 0 0 1 2-2z" />
            <path d="M14 3.5V8h4" />
            <path d="M9 12h6" />
            <path d="M9 16h6" />
          </>
        ) : index === 1 ? (
          <>
            <path d="M14.5 6.5 17.5 3.5 20.5 6.5 17.5 9.5" />
            <path d="M5 19l5.5-5.5" />
            <path d="M4 20l3-1 9-9-2-2-9 9-1 3z" />
          </>
        ) : index === 2 ? (
          <>
            <circle cx="9" cy="9" r="3" />
            <path d="M3.5 18a5.5 5.5 0 0 1 11 0" />
            <circle cx="17" cy="8" r="2.5" />
            <path d="M15 18a4.5 4.5 0 0 1 6 0" />
          </>
        ) : (
          <>
            <circle cx="12" cy="12" r="8" />
            <path d="M12 8v8" />
            <path d="M9.5 10.5c0-1 1.1-1.8 2.5-1.8s2.5.8 2.5 1.8-1.1 1.8-2.5 1.8-2.5.8-2.5 1.8 1.1 1.8 2.5 1.8 2.5-.8 2.5-1.8" />
          </>
        )}
      </svg>
    </span>
  );
}

function FaqChipIcon({ topic }: { topic: string }) {
  return (
    <span className="faq-chip-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        {topic === 'all' ? (
          <>
            <rect x="4" y="4" width="6" height="6" rx="1" />
            <rect x="14" y="4" width="6" height="6" rx="1" />
            <rect x="4" y="14" width="6" height="6" rx="1" />
            <rect x="14" y="14" width="6" height="6" rx="1" />
          </>
        ) : topic === 'membership' ? (
          <>
            <circle cx="12" cy="8" r="3" />
            <path d="M5 19a7 7 0 0 1 14 0" />
          </>
        ) : topic === 'pricing' ? (
          <>
            <path d="M12 3v18" />
            <path d="M17 7.5c0-1.7-2.2-3-5-3s-5 1.3-5 3 2.2 3 5 3 5 1.3 5 3-2.2 3-5 3-5-1.3-5-3" />
          </>
        ) : topic === 'repairs' ? (
          <>
            <path d="m14 6 4 4" />
            <path d="m5 19 6.5-6.5" />
            <path d="m3 21 3.5-1 11-11-2.5-2.5-11 11L3 21z" />
          </>
        ) : topic === 'records' ? (
          <>
            <path d="M8 3.5h6l4 4V20a1 1 0 0 1-1 1H8a2 2 0 0 1-2-2V5.5a2 2 0 0 1 2-2z" />
            <path d="M14 3.5V8h4" />
            <path d="M9 12h6" />
            <path d="M9 16h6" />
          </>
        ) : (
          <>
            <rect x="4" y="5" width="16" height="15" rx="2" />
            <path d="M8 3v4M16 3v4M4 10h16" />
          </>
        )}
      </svg>
    </span>
  );
}

function ListItemText({
  path,
  value,
}: {
  path: string;
  value: string;
}) {
  return (
    <span>
      <EditableText path={path}>{value}</EditableText>
    </span>
  );
}

export default function CarKeepersClubRenderer({
  content,
  routePath,
}: SiteRendererProps) {
  const { editing } = useEditMode();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const data = useMemo(() => mergeContent(content), [content]);
  const locale: Locale = routePath?.startsWith('/pt') ? 'pt' : 'en';
  const current = locale === 'pt' ? data.pt : data.en;
  const anchors = ANCHORS[locale];
  const sectionPath =
    locale === 'pt'
      ? {
          seo: 'ptSeo',
          nav: 'ptNav',
          hero: 'ptHero',
          services: 'ptServices',
          decision: 'ptDecision',
          pricing: 'ptPricing',
          location: 'ptLocation',
          faq: 'ptFaq',
          cta: 'ptCta',
          footer: 'ptFooter',
        }
      : {
          seo: 'enSeo',
          nav: 'enNav',
          hero: 'enHero',
          services: 'enServices',
          decision: 'enDecision',
          pricing: 'enPricing',
          location: 'enLocation',
          faq: 'enFaq',
          cta: 'enCta',
          footer: 'enFooter',
        };
  const switchHref = locale === 'pt' ? '/madeira' : '/pt/madeira';

  const [membershipPlan, setMembershipPlan] = useState<MembershipPlan>('fiveYear');
  const [mobilePricingCard, setMobilePricingCard] = useState<'membership' | 'repair'>('membership');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [faqTopic, setFaqTopic] = useState('all');
  const [faqQuery, setFaqQuery] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const deferredFaqQuery = useDeferredValue(faqQuery.trim().toLowerCase());

  useEffect(() => {
    trackEvent('page_view');
  }, []);

  useEffect(() => {
    document.title = current.seo.metaTitle;
    document.documentElement.lang = locale === 'pt' ? 'pt-PT' : 'en';
    const description = current.seo.metaDescription?.trim();
    if (!description) return;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);
  }, [current.seo.metaDescription, current.seo.metaTitle, locale]);

  useEffect(() => {
    setMembershipPlan('fiveYear');
    setMobilePricingCard('membership');
    setMobileMenuOpen(false);
    setFaqTopic('all');
    setFaqQuery('');
    setOpenFaqIndex(0);
  }, [locale]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const lines = Array.from(root.querySelectorAll<HTMLElement>('.hero-accent, .services-rule'));
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;

    const resetLines = () => {
      lines.forEach((line) => line.style.setProperty('--line-offset', '0px'));
    };

    if (editing || lines.length === 0) {
      resetLines();
      return undefined;
    }

    const updateLines = () => {
      if (prefersReducedMotion.matches) {
        resetLines();
        return;
      }

      const viewportHeight = window.innerHeight || 1;
      lines.forEach((line, index) => {
        const rect = line.getBoundingClientRect();
        const lineCenter = rect.top + rect.height / 2;
        const normalizedOffset = (lineCenter - viewportHeight * 0.5) / viewportHeight;
        const clampedOffset = Math.max(-1, Math.min(1, normalizedOffset * 2.1));
        const direction = index % 2 === 0 ? -1 : 1;
        const travel = line.classList.contains('hero-accent') ? 22 : 16;
        line.style.setProperty('--line-offset', `${(clampedOffset * travel * direction).toFixed(2)}px`);
      });
    };

    const queueUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateLines();
      });
    };

    updateLines();
    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);
    if (typeof prefersReducedMotion.addEventListener === 'function') {
      prefersReducedMotion.addEventListener('change', queueUpdate);
    } else {
      prefersReducedMotion.addListener(queueUpdate);
    }

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', queueUpdate);
      window.removeEventListener('resize', queueUpdate);
      if (typeof prefersReducedMotion.removeEventListener === 'function') {
        prefersReducedMotion.removeEventListener('change', queueUpdate);
      } else {
        prefersReducedMotion.removeListener(queueUpdate);
      }
      resetLines();
    };
  }, [editing, locale]);

  const logoImage = data.media?.logo as ImageValue | undefined;
  const workshopExterior = data.media?.workshopExterior as ImageValue | undefined;
  const workshopCanico = data.media?.workshopCanico as ImageValue | undefined;
  const safeLogoImage = logoImage?.url
    ? { ...logoImage, url: sanitizeMediaUrl(logoImage.url) ?? LOGO_PNG_URL }
    : { url: LOGO_PNG_URL, alt: 'Car Keepers Club' };
  const safeWorkshopExterior = workshopExterior?.url
    ? { ...workshopExterior, url: sanitizeMediaUrl(workshopExterior.url) ?? WORKSHOP_JPG_URL }
    : { url: WORKSHOP_JPG_URL, alt: 'Car Keepers Club Madeira workshop exterior' };
  const safeWorkshopCanico = workshopCanico?.url
    ? { ...workshopCanico, url: sanitizeMediaUrl(workshopCanico.url) ?? WORKSHOP_WEBP_URL }
    : { url: WORKSHOP_WEBP_URL, alt: 'Car Keepers Club workshop in Caniço, Madeira' };

  const whatsappHref = sanitizeWhatsAppHref(data.brand?.whatsapp);
  const homeHref = safeHref(data.brand?.homeHref);
  const privacyHref = safeHref(data.brand?.privacyHref);
  const termsHref = safeHref(data.brand?.termsHref);
  const addressHref = safeHref(data.brand?.addressHref);
  const coordinatesHref = safeHref(data.brand?.coordinatesHref);
  const mailtoHref = data.brand?.email ? `mailto:${data.brand.email}` : '#';

  const handleTrackedClick =
    (type: 'whatsapp_click' | 'cta_click' | 'directions_click') =>
    (event: MouseEvent<HTMLElement>) => {
      preventEditNavigation(event, editing);
      if (!editing) trackEvent(type);
    };

  const handleMobileNavClick = (event: MouseEvent<HTMLElement>) => {
    preventEditNavigation(event, editing);
    setMobileMenuOpen(false);
  };

  const visibleFaqItems = editing
    ? current.faq.items.map((item, originalIndex) => ({ item, originalIndex }))
    : current.faq.items
        .map((item, originalIndex) => ({ item, originalIndex }))
        .filter(({ item }) => {
          const matchesTopic = faqTopic === 'all' || item.topic.includes(faqTopic);
          const haystack = `${item.question} ${item.answer} ${item.keywords ?? ''}`
            .toLowerCase()
            .trim();
          const matchesQuery = !deferredFaqQuery || haystack.includes(deferredFaqQuery);
          return matchesTopic && matchesQuery;
        });

  useEffect(() => {
    if (visibleFaqItems.length === 0) {
      setOpenFaqIndex(-1);
      return;
    }
    if (openFaqIndex < 0 || openFaqIndex >= visibleFaqItems.length) {
      setOpenFaqIndex(0);
    }
  }, [openFaqIndex, visibleFaqItems.length]);

  const membershipPlanContent =
    membershipPlan === 'monthly'
      ? {
          amount: current.pricing.monthlyAmount,
          period: current.pricing.monthlyPeriod,
          copy: current.pricing.monthlyCopy,
          badge: current.pricing.monthlyBadge ?? '',
          benefits: [
            current.pricing.monthlyBenefit1,
            current.pricing.monthlyBenefit2,
            current.pricing.monthlyBenefit3,
          ],
          summaries: [
            current.pricing.monthlySummary1,
            current.pricing.monthlySummary2,
          ],
        }
      : {
          amount: current.pricing.fiveYearAmount,
          period: current.pricing.fiveYearPeriod,
          copy: current.pricing.fiveYearCopy,
          badge: current.pricing.fiveYearBadge ?? '',
          benefits: [
            current.pricing.fiveYearBenefit1,
            current.pricing.fiveYearBenefit2,
            current.pricing.fiveYearBenefit3,
          ],
          summaries: [
            current.pricing.fiveYearSummary1,
            current.pricing.fiveYearSummary2,
          ],
        };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: pageStylesByLocale[locale] }} />
      <style>{`
        #${SITE_ROOT_ID} {
          position: relative;
          isolation: isolate;
        }

        #${SITE_ROOT_ID} .brand-logo,
        #${SITE_ROOT_ID} .hero-card-image,
        #${SITE_ROOT_ID} .image-band-image {
          display: block;
          width: 100%;
        }

        #${SITE_ROOT_ID} .brand-logo {
          height: 2.75rem;
          width: auto;
        }

        #${SITE_ROOT_ID} .hero-card-image {
          height: 45rem;
        }

        #${SITE_ROOT_ID} .hero-card-image > img {
          height: 100%;
          width: 100%;
          object-fit: cover;
        }

        #${SITE_ROOT_ID} .hero-card-content {
          max-width: 100%;
        }

        #${SITE_ROOT_ID} .image-band-image {
          height: 100%;
          object-fit: cover;
        }

        #${SITE_ROOT_ID} .image-band-image > img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        #${SITE_ROOT_ID} .pricing-card-switch {
          display: none;
          width: 100%;
          margin: 0 0 1rem;
        }

        #${SITE_ROOT_ID} .decision-section {
          clear: both;
          padding: 4.5rem 0;
        }

        #${SITE_ROOT_ID} .decision-shell {
          width: min(80rem, calc(100vw - 4rem));
          margin-inline: auto;
          padding: clamp(2rem, 4vw, 3rem);
        }

        #${SITE_ROOT_ID} .decision-body {
          display: grid;
          grid-template-columns: minmax(0, 1.22fr) minmax(16.25rem, 0.78fr);
          gap: 0.875rem;
          align-items: stretch;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-panel {
          padding: 2rem 2.375rem;
          border: 0.0625rem solid #dce6f4;
          border-radius: 1.625rem;
          background: rgba(255, 255, 255, 0.92);
          box-shadow: 0 1.125rem 2.75rem rgba(26, 55, 104, 0.08);
        }

        #${SITE_ROOT_ID} .decision-body .matrix-list {
          display: grid;
          gap: 0;
          margin: 0;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-row {
          display: grid;
          grid-template-columns: 4.125rem minmax(0, 1fr);
          align-items: center;
          gap: 1rem;
          padding: 1.25rem 0;
          border-bottom: 0.0625rem solid #dde6f2;
          color: #6f89a4;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-row:last-child {
          border-bottom: 0;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-text {
          display: grid;
          grid-template-columns: minmax(8.25rem, 0.85fr) minmax(13.75rem, 1fr);
          align-items: center;
          gap: 1.125rem;
          font-size: 1.02rem;
          line-height: 1.45;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-row strong {
          color: #16386d;
          font-size: 1.08rem;
          letter-spacing: -0.01em;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-icon {
          width: 3rem;
          height: 3rem;
          border-radius: 62.4375rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #2380d9;
          background: linear-gradient(180deg, #eef5ff, #e6f0ff);
          box-shadow: inset 0 0.0625rem 0 rgba(255,255,255,0.9);
        }

        #${SITE_ROOT_ID} .decision-body .matrix-icon svg {
          width: 1.5rem;
          height: 1.5rem;
          stroke: currentColor;
          fill: none;
          stroke-width: 1.9;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        #${SITE_ROOT_ID} .decision-body .matrix-copy {
          color: #6f89a4;
          max-width: 24ch;
        }

        #${SITE_ROOT_ID} .decision-body .image-band {
          overflow: hidden;
          width: 100%;
          max-width: 26.875rem;
          justify-self: end;
          align-self: stretch;
          border-radius: 1.625rem;
          border: 0.0625rem solid #dce6f4;
          background: #f4f8ff;
          box-shadow: 0 1.125rem 2.75rem rgba(26, 55, 104, 0.08);
        }

        #${SITE_ROOT_ID} .decision-body .image-band-image,
        #${SITE_ROOT_ID} .decision-body .image-band-image > img {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 100%;
          object-fit: cover;
          object-position: center;
        }

        @media (min-width: 62.5625rem) {
          #${SITE_ROOT_ID} .hero h1 {
            font-size: clamp(4.4rem, 7.4vw, 7rem);
          }

          #${SITE_ROOT_ID} .decision-body {
            display: flex;
            align-items: stretch;
            justify-content: space-between;
            gap: 0.875rem;
          }

          #${SITE_ROOT_ID} .decision-body .matrix-panel {
            flex: 1 1 auto;
            min-width: 0;
          }

          #${SITE_ROOT_ID} .decision-body .image-band {
            flex: 0 0 26.875rem;
            width: 26.875rem;
            max-width: 26.875rem;
            min-width: 26.875rem;
            align-self: stretch;
          }

          #${SITE_ROOT_ID} .decision-body .image-band-image {
            min-height: 100%;
          }
        }

        #${SITE_ROOT_ID} .faq-head h2 > [data-locusly-edit-path] {
          color: inherit;
        }

        #${SITE_ROOT_ID} .faq-dot {
          display: inline-block;
          color: var(--accent-dark);
          transform-origin: center 72%;
          animation: title-dot-pop 800ms cubic-bezier(0.22, 1, 0.36, 1) 140ms both, title-dot-pulse 3.6s ease-in-out 1.1s infinite;
          will-change: transform;
        }

        @media (prefers-reduced-motion: reduce) {
          #${SITE_ROOT_ID} .faq-dot {
            animation: none !important;
            transform: none !important;
          }
        }

        @media (min-width: 45.0625rem) and (max-width: 62.5rem) {
          #${SITE_ROOT_ID} .decision-body {
            grid-template-columns: 1fr;
          }

          #${SITE_ROOT_ID} .decision-body .image-band {
            display: none;
          }
        }

        @media (max-width: 45rem) {
          #${SITE_ROOT_ID} .pricing-card-switch {
            display: inline-flex;
          }

          #${SITE_ROOT_ID} .pricing-showcase-card.is-mobile-hidden {
            display: none;
          }

          #${SITE_ROOT_ID} .decision-shell {
            width: min(100%, calc(100vw - 1.75rem));
            padding: 1.5rem 1.125rem;
          }

          #${SITE_ROOT_ID} .decision-body {
            grid-template-columns: 1fr;
          }

          #${SITE_ROOT_ID} .decision-body .matrix-panel {
            padding: 1.125rem 1.375rem;
          }

          #${SITE_ROOT_ID} .decision-body .matrix-row {
            grid-template-columns: 3.5rem 1fr;
            gap: 0.875rem 1rem;
            align-items: center;
          }

          #${SITE_ROOT_ID} .decision-body .matrix-text {
            grid-template-columns: 1fr;
            gap: 0.25rem;
          }

          #${SITE_ROOT_ID} .decision-body .image-band {
            width: min(100%, 32.5rem);
            max-width: none;
            justify-self: center;
            align-self: start;
          }
        }

        #${SITE_ROOT_ID} .mobile-menu-button {
          display: none;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 2.625rem;
          padding: 0 0.85rem;
          border: 0.0625rem solid rgba(45, 115, 175, 0.22);
          border-radius: 0.8rem;
          background: #fff;
          color: #10255a;
          cursor: pointer;
          font: inherit;
          font-size: 0.92rem;
          font-weight: 800;
          line-height: 1;
          box-shadow: 0 0.6rem 1.25rem rgba(12, 35, 83, 0.06);
        }

        #${SITE_ROOT_ID} .mobile-menu-button:hover,
        #${SITE_ROOT_ID} .mobile-menu-button.is-open {
          border-color: rgba(31, 103, 223, 0.45);
          color: #1e63df;
        }

        #${SITE_ROOT_ID} .mobile-menu-lines {
          display: inline-flex;
          width: 1.125rem;
          flex: 0 0 auto;
          flex-direction: column;
          gap: 0.25rem;
        }

        #${SITE_ROOT_ID} .mobile-menu-lines span {
          display: block;
          width: 1.125rem;
          height: 0.125rem;
          border-radius: 999px;
          background: currentColor;
          transform-origin: center;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }

        #${SITE_ROOT_ID} .mobile-menu-button.is-open .mobile-menu-lines span:nth-child(1) {
          transform: translateY(0.375rem) rotate(45deg);
        }

        #${SITE_ROOT_ID} .mobile-menu-button.is-open .mobile-menu-lines span:nth-child(2) {
          opacity: 0;
        }

        #${SITE_ROOT_ID} .mobile-menu-button.is-open .mobile-menu-lines span:nth-child(3) {
          transform: translateY(-0.375rem) rotate(-45deg);
        }

        #${SITE_ROOT_ID} .mobile-menu-label {
          white-space: nowrap;
        }

        #${SITE_ROOT_ID} .mobile-nav-panel {
          display: none;
        }

        @media (max-width: 62.5rem) {
          #${SITE_ROOT_ID} .container.nav {
            position: relative;
            gap: 0.75rem;
          }

          #${SITE_ROOT_ID} .nav-tools {
            gap: 0.5rem;
          }

          #${SITE_ROOT_ID} .mobile-menu-button {
            display: inline-flex;
          }

          #${SITE_ROOT_ID} .mobile-nav-panel {
            position: absolute;
            top: calc(100% + 0.625rem);
            right: 0.875rem;
            left: 0.875rem;
            z-index: 60;
            padding: 0.45rem;
            border: 0.0625rem solid rgba(45, 115, 175, 0.16);
            border-radius: 1rem;
            background: rgba(255, 255, 255, 0.98);
            box-shadow: 0 1.25rem 3rem rgba(12, 35, 83, 0.16);
          }

          #${SITE_ROOT_ID} .mobile-nav-panel.is-open {
            display: grid;
          }

          #${SITE_ROOT_ID} .mobile-nav-panel a {
            display: flex;
            align-items: center;
            min-height: 2.75rem;
            padding: 0.75rem 0.85rem;
            border-radius: 0.75rem;
            color: #263d67;
            font-size: 0.98rem;
            font-weight: 800;
            text-decoration: none;
          }

          #${SITE_ROOT_ID} .mobile-nav-panel a:hover {
            background: rgba(31, 103, 223, 0.08);
            color: #0c2353;
          }
        }

        @media (max-width: 45rem) {
          #${SITE_ROOT_ID} .mobile-menu-label {
            display: none;
          }

          #${SITE_ROOT_ID} .mobile-menu-button {
            width: 2.625rem;
            padding: 0;
          }
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .mobile-menu-button,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-menu-button,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .mobile-menu-button {
          display: inline-flex !important;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .nav-links,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-links,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .nav-links {
          display: none !important;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .decision-shell,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-shell,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-shell {
          width: min(100%, calc(100vw - 1.75rem));
          padding: 1.5rem 1.125rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .decision-body,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body {
          grid-template-columns: 1fr;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .decision-body .matrix-panel,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .matrix-panel,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .matrix-panel {
          padding: 1.125rem 1.375rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .decision-body .matrix-row,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .matrix-row,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .matrix-row {
          grid-template-columns: 3.5rem 1fr;
          gap: 0.875rem 1rem;
          align-items: center;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .decision-body .matrix-text,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .matrix-text,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .matrix-text {
          grid-template-columns: 1fr;
          gap: 0.25rem;
        }

        .locusly-editor-viewport-phone #${SITE_ROOT_ID} .decision-body .image-band,
        html[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .image-band,
        body[data-locusly-editor-viewport="phone"] #${SITE_ROOT_ID} .decision-body .image-band {
          width: min(100%, 32.5rem);
          max-width: none;
          justify-self: center;
          align-self: start;
        }
      `}</style>
      <div id={SITE_ROOT_ID} ref={rootRef}>
        <header className="site-header" aria-label={locale === 'pt' ? 'Cabeçalho do site' : 'Site header'}>
          <div className="container nav">
            <a
              className="brand"
              href={homeHref}
              aria-label={locale === 'pt' ? 'Página inicial do Car Keepers Club' : 'Car Keepers Club homepage'}
              onClick={(event) => preventEditNavigation(event, editing)}
            >
              <EditableImage
                path="media.logo"
                value={safeLogoImage}
                className="brand-logo"
                emptyLabel="Adicionar logótipo"
              />
            </a>

            <nav className="nav-links" aria-label={locale === 'pt' ? 'Navegação principal' : 'Main navigation'}>
              <a href={anchors.services} onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.nav}.servicesLabel`} richText={false}>
                  {current.nav.servicesLabel}
                </EditableText>
              </a>
              <a href={anchors.about} onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.nav}.aboutLabel`} richText={false}>
                  {current.nav.aboutLabel}
                </EditableText>
              </a>
              <a href={anchors.pricing} onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.nav}.pricingLabel`} richText={false}>
                  {current.nav.pricingLabel}
                </EditableText>
              </a>
              <a href={anchors.location} onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.nav}.locationLabel`} richText={false}>
                  {current.nav.locationLabel}
                </EditableText>
              </a>
              <a href={anchors.faq} onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.nav}.faqLabel`} richText={false}>
                  {current.nav.faqLabel}
                </EditableText>
              </a>
            </nav>

            <div className="nav-tools">
              <a
                className="lang-switch"
                href={switchHref}
                aria-label={locale === 'pt' ? 'Versão em inglês' : 'Versão em português'}
                onClick={(event) => preventEditNavigation(event, editing)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
                </svg>
                <EditableText path={`${sectionPath.nav}.langSwitchLabel`} richText={false}>
                  {current.nav.langSwitchLabel}
                </EditableText>
              </a>

              <a
                className="button button-primary"
                href={whatsappHref ?? '#'}
                onClick={handleTrackedClick('whatsapp_click')}
              >
                <EditableText path={`${sectionPath.nav}.whatsappLabel`} richText={false}>
                  {current.nav.whatsappLabel}
                </EditableText>
              </a>

              <button
                className={`mobile-menu-button${mobileMenuOpen ? ' is-open' : ''}`}
                type="button"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="carKeepersMobileNav"
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                <span className="mobile-menu-lines" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="mobile-menu-label">Menu</span>
              </button>
            </div>

            <nav
              className={`mobile-nav-panel${mobileMenuOpen ? ' is-open' : ''}`}
              id="carKeepersMobileNav"
              aria-label={locale === 'pt' ? 'NavegaÃ§Ã£o mÃ³vel' : 'Mobile navigation'}
            >
              <a href={anchors.services} onClick={handleMobileNavClick}>
                <EditableText path={`${sectionPath.nav}.servicesLabel`} richText={false}>
                  {current.nav.servicesLabel}
                </EditableText>
              </a>
              <a href={anchors.about} onClick={handleMobileNavClick}>
                <EditableText path={`${sectionPath.nav}.aboutLabel`} richText={false}>
                  {current.nav.aboutLabel}
                </EditableText>
              </a>
              <a href={anchors.pricing} onClick={handleMobileNavClick}>
                <EditableText path={`${sectionPath.nav}.pricingLabel`} richText={false}>
                  {current.nav.pricingLabel}
                </EditableText>
              </a>
              <a href={anchors.location} onClick={handleMobileNavClick}>
                <EditableText path={`${sectionPath.nav}.locationLabel`} richText={false}>
                  {current.nav.locationLabel}
                </EditableText>
              </a>
              <a href={anchors.faq} onClick={handleMobileNavClick}>
                <EditableText path={`${sectionPath.nav}.faqLabel`} richText={false}>
                  {current.nav.faqLabel}
                </EditableText>
              </a>
            </nav>
          </div>
        </header>

        <main>
          <section className="hero">
            <div className="container hero-grid">
              <div>
                <h1>
                  <EditableText path={`${sectionPath.hero}.title`} as="span" multiline>
                    {current.hero.title.replace(/\.$/, '')}
                  </EditableText>
                  <span className="headline-dot">.</span>
                </h1>
                <span className="hero-accent" aria-hidden="true" />
                <EditableText path={`${sectionPath.hero}.lead`} as="p" className="hero-lead" multiline>
                  {current.hero.lead}
                </EditableText>

                <div className="hero-actions">
                  <a
                    className="button button-primary"
                    href={whatsappHref ?? '#'}
                    onClick={handleTrackedClick('whatsapp_click')}
                  >
                    <span className="button-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <rect x="4" y="5" width="16" height="15" rx="2" />
                        <path d="M8 3v4M16 3v4M4 10h16M8 14h3" />
                      </svg>
                    </span>
                    <EditableText path={`${sectionPath.hero}.primaryCta`} richText={false}>
                      {current.hero.primaryCta}
                    </EditableText>
                  </a>
                  <a
                    className="button button-secondary"
                    href={anchors.pricing}
                    onClick={handleTrackedClick('cta_click')}
                  >
                    <span className="button-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 13h18" />
                        <path d="M6 13l1.6-5a2 2 0 0 1 1.9-1.4h5a2 2 0 0 1 1.9 1.4L18 13" />
                        <path d="M5 13v5M19 13v5M7 18h10" />
                        <circle cx="7.5" cy="13.5" r="1" />
                        <circle cx="16.5" cy="13.5" r="1" />
                      </svg>
                    </span>
                    <EditableText path={`${sectionPath.hero}.secondaryCta`} richText={false}>
                      {current.hero.secondaryCta}
                    </EditableText>
                  </a>
                </div>
              </div>

              <aside className="hero-card" aria-label={locale === 'pt' ? 'Foto da oficina e histórico de propriedade' : 'Workshop photo and ownership history highlights'}>
                <EditableImage
                  path="media.workshopExterior"
                  value={safeWorkshopExterior}
                  className="hero-card-image"
                  emptyLabel="Adicionar imagem hero"
                />
                <div className="hero-card-content">
                  <div className="hero-card-panel">
                    <span className="hero-card-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 13h18M6 13l1.6-5a2 2 0 0 1 1.9-1.4h5a2 2 0 0 1 1.9 1.4L18 13" />
                        <path d="M5 13v5M19 13v5M7 18h10" />
                        <circle cx="7.5" cy="13.5" r="1" />
                        <circle cx="16.5" cy="13.5" r="1" />
                      </svg>
                    </span>
                    <div className="hero-card-copy">
                      <EditableText path={`${sectionPath.hero}.cardTitle`} as="h2">
                        {current.hero.cardTitle}
                      </EditableText>
                      <EditableText path={`${sectionPath.hero}.cardBody`} as="p" multiline>
                        {current.hero.cardBody}
                      </EditableText>
                    </div>
                  </div>
                  <ul className="hero-tags">
                    {[
                      current.hero.tag1,
                      current.hero.tag2,
                      current.hero.tag3,
                      current.hero.tag4,
                    ].map((value, index) => (
                      <li key={index}>
                        <span className="hero-tag-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M12 21s-6.5-3.6-6.5-9.4V5.8L12 3l6.5 2.8v5.8C18.5 17.4 12 21 12 21Z" />
                            <path d="m9.5 12 1.7 1.7 3.3-3.5" />
                          </svg>
                        </span>
                        <EditableText path={`${sectionPath.hero}.tag${index + 1}`} as="span">
                          {value}
                        </EditableText>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>

            <div className="container">
              <div className="hero-trust" aria-label={locale === 'pt' ? 'Pontos de confiança' : 'Trust points'}>
                  <EditableText path={`${sectionPath.hero}.quickLinksLabel`} as="p">
                  {current.hero.quickLinksLabel}
                </EditableText>
                <ul className="trust-list">
                  {[
                    [anchors.services, current.hero.quickLink1, `${sectionPath.hero}.quickLink1`],
                    [anchors.pricing, current.hero.quickLink2, `${sectionPath.hero}.quickLink2`],
                    [anchors.location, current.hero.quickLink3, `${sectionPath.hero}.quickLink3`],
                    [anchors.faq, current.hero.quickLink4, `${sectionPath.hero}.quickLink4`],
                  ].map(([href, label, path]) => (
                    <li key={String(path)}>
                      <a href={String(href)} onClick={(event) => preventEditNavigation(event, editing)}>
                        <EditableText path={String(path)} richText={false}>
                          {String(label)}
                        </EditableText>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="section services-section" id={locale === 'pt' ? 'servicos' : 'services'}>
            <div className="container">
              <div className="section-header services-header">
                <div className="section-kicker">
                  <EditableText path={`${sectionPath.services}.kicker`} as="span">
                    {current.services.kicker}
                  </EditableText>
                </div>
                <h2>
                  <EditableText path={`${sectionPath.services}.title`} as="span" richText={false}>
                    {current.services.title.replace(/\.$/, '')}
                  </EditableText>
                  <span className="services-dot">.</span>
                </h2>
                <SectionRule />
                <EditableText path={`${sectionPath.services}.body`} as="p" multiline>
                  {current.services.body}
                </EditableText>
              </div>

              <div className="grid-3 services-grid">
                {current.services.items.map((item, index) => (
                  <article
                    className={`card service-card${index === 1 ? ' featured' : ''}`}
                    key={`${locale}-service-${index}`}
                  >
                    <div className="service-meta">
                      <div className="service-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                          {index === 0 ? (
                            <path d="M12 3l7 3v5c0 5-3.4 8.7-7 10-3.6-1.3-7-5-7-10V6l7-3z" />
                          ) : index === 1 ? (
                            <>
                              <path d="M14.5 6.5l3-3 3 3-3 3" />
                              <path d="M9.5 17.5l-3 3-3-3 3-3" />
                              <path d="M8 16l8-8" />
                              <path d="M11 4H4v7" />
                              <path d="M20 13v7h-7" />
                            </>
                          ) : (
                            <>
                              <path d="M16 19a4 4 0 0 0-8 0" />
                              <circle cx="12" cy="9" r="3" />
                              <path d="M22 19a4 4 0 0 0-6-3.5" />
                              <path d="M8 15.5A4 4 0 0 0 2 19" />
                            </>
                          )}
                        </svg>
                      </div>
                      <div className="icon">
                        <EditableText path={`${sectionPath.services}.items.${index}.number`} as="span" richText={false}>
                          {item.number}
                        </EditableText>
                      </div>
                    </div>
                    <EditableText path={`${sectionPath.services}.items.${index}.title`} as="h3" richText={false}>
                      {item.title}
                    </EditableText>
                    <EditableText path={`${sectionPath.services}.items.${index}.body`} as="p" multiline>
                      {item.body}
                    </EditableText>
                    <ul className="list">
                      <li>
                        <ListItemText
                          path={`${sectionPath.services}.items.${index}.bullet1`}
                          value={item.bullet1}
                        />
                      </li>
                      <li>
                        <ListItemText
                          path={`${sectionPath.services}.items.${index}.bullet2`}
                          value={item.bullet2}
                        />
                      </li>
                      <li>
                        <ListItemText
                          path={`${sectionPath.services}.items.${index}.bullet3`}
                          value={item.bullet3}
                        />
                      </li>
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section
            className="section decision-section"
            id={locale === 'pt' ? 'sobre' : 'about'}
            aria-label={locale === 'pt' ? 'Filosofia de serviço' : 'Service philosophy'}
          >
            <div className="decision-shell">
              <div className="section-header services-header">
                <div className="section-kicker">
                  <EditableText path={`${sectionPath.decision}.kicker`} as="span">
                    {current.decision.kicker}
                  </EditableText>
                </div>
                <h2>
                  <EditableText path={`${sectionPath.decision}.title`} as="span" richText={false}>
                    {current.decision.title.replace(/\.$/, '')}
                  </EditableText>
                  <span className="services-dot">.</span>
                </h2>
                <SectionRule />
                <EditableText path={`${sectionPath.decision}.body`} as="p" multiline>
                  {current.decision.body}
                </EditableText>
              </div>

              <div className="decision-body">
                <div className="matrix-panel">
                  <div className="matrix-list">
                    {[
                      ['row1Label', 'row1Value'],
                      ['row2Label', 'row2Value'],
                      ['row3Label', 'row3Value'],
                      ['row4Label', 'row4Value'],
                    ].map(([labelKey, valueKey], index) => (
                      <div className="matrix-row" key={index}>
                        <DecisionMatrixIcon index={index} />
                        <div className="matrix-text">
                          <strong>
                            <EditableText path={`${sectionPath.decision}.${labelKey}`} as="span">
                              {current.decision[labelKey as keyof typeof current.decision] as string}
                            </EditableText>
                          </strong>
                          <span className="matrix-copy">
                            <EditableText path={`${sectionPath.decision}.${valueKey}`} as="span">
                              {current.decision[valueKey as keyof typeof current.decision] as string}
                            </EditableText>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="image-band">
                  <EditableImage
                    path="media.workshopCanico"
                    value={safeWorkshopCanico}
                    className="image-band-image"
                    emptyLabel="Adicionar imagem da oficina"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="section" id={locale === 'pt' ? 'precos' : 'pricing'}>
            <div className="container">
              <div className="section-header services-header">
                <div className="section-kicker">
                  <EditableText path={`${sectionPath.pricing}.kicker`} as="span">
                    {current.pricing.kicker}
                  </EditableText>
                </div>
                <h2>
                  <EditableText path={`${sectionPath.pricing}.title`} as="span" richText={false}>
                    {current.pricing.title.replace(/\.$/, '')}
                  </EditableText>
                  <span className="services-dot">.</span>
                </h2>
                <SectionRule />
                <EditableText path={`${sectionPath.pricing}.body`} as="p" multiline>
                  {current.pricing.body}
                </EditableText>
              </div>

              <div className="grid-2">
                <div
                  className="plan-switch pricing-card-switch"
                  role="group"
                  aria-label={locale === 'pt' ? 'Escolher tabela de preços' : 'Choose pricing card'}
                >
                  <button
                    className={`button button-secondary${mobilePricingCard === 'membership' ? ' is-active' : ''}`}
                    type="button"
                    aria-pressed={mobilePricingCard === 'membership'}
                    onClick={() => setMobilePricingCard('membership')}
                  >
                    <EditableText path={`${sectionPath.pricing}.membershipTitle`} richText={false}>
                      {current.pricing.membershipTitle}
                    </EditableText>
                  </button>
                  <button
                    className={`button button-secondary${mobilePricingCard === 'repair' ? ' is-active' : ''}`}
                    type="button"
                    aria-pressed={mobilePricingCard === 'repair'}
                    onClick={() => setMobilePricingCard('repair')}
                  >
                    <EditableText path={`${sectionPath.pricing}.repairTitle`} richText={false}>
                      {current.pricing.repairTitle}
                    </EditableText>
                  </button>
                </div>

                <article
                  className={`card pricing-showcase-card membership-card${
                    !editing && mobilePricingCard !== 'membership' ? ' is-mobile-hidden' : ''
                  }`}
                >
                  <EditableText path={`${sectionPath.pricing}.membershipTitle`} as="h3">
                    {current.pricing.membershipTitle}
                  </EditableText>
                  <div className="plan-switch" role="group" aria-label={locale === 'pt' ? 'Escolher plano de adesão' : 'Choose membership plan'}>
                    <button
                      className={`button button-secondary${membershipPlan === 'fiveYear' ? ' is-active' : ''}`}
                      type="button"
                      aria-pressed={membershipPlan === 'fiveYear'}
                      onClick={() => setMembershipPlan('fiveYear')}
                    >
                      <EditableText path={`${sectionPath.pricing}.planFiveYearLabel`} richText={false}>
                        {current.pricing.planFiveYearLabel}
                      </EditableText>
                    </button>
                    <button
                      className={`button button-secondary${membershipPlan === 'monthly' ? ' is-active' : ''}`}
                      type="button"
                      aria-pressed={membershipPlan === 'monthly'}
                      onClick={() => setMembershipPlan('monthly')}
                    >
                      <EditableText path={`${sectionPath.pricing}.planMonthlyLabel`} richText={false}>
                        {current.pricing.planMonthlyLabel}
                      </EditableText>
                    </button>
                  </div>
                  <div className="pricing-price-row">
                    <div className="price">
                      <EditableText
                        path={`${sectionPath.pricing}.${membershipPlan === 'monthly' ? 'monthlyAmount' : 'fiveYearAmount'}`}
                        as="strong"
                        richText={false}
                      >
                        {membershipPlanContent.amount}
                      </EditableText>
                      <EditableText
                        path={`${sectionPath.pricing}.${membershipPlan === 'monthly' ? 'monthlyPeriod' : 'fiveYearPeriod'}`}
                        as="span"
                        richText={false}
                      >
                        {membershipPlanContent.period}
                      </EditableText>
                    </div>
                    {membershipPlanContent.badge ? (
                      <span className="membership-badge">
                        <span className="membership-badge-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24">
                            <path d="m12 2.6 2.83 5.73 6.33.92-4.58 4.46 1.08 6.29L12 17.02 6.34 20l1.08-6.29L2.84 9.25l6.33-.92L12 2.6z" />
                          </svg>
                        </span>
                        <EditableText
                          path={`${sectionPath.pricing}.${membershipPlan === 'monthly' ? 'monthlyBadge' : 'fiveYearBadge'}`}
                          as="span"
                          richText={false}
                        >
                          {membershipPlanContent.badge}
                        </EditableText>
                      </span>
                    ) : null}
                  </div>
                  <EditableText
                    path={`${sectionPath.pricing}.${membershipPlan === 'monthly' ? 'monthlyCopy' : 'fiveYearCopy'}`}
                    as="p"
                    className="pricing-copy"
                    multiline
                  >
                    {membershipPlanContent.copy}
                  </EditableText>
                  <ul className="list pricing-benefits">
                    {membershipPlanContent.benefits.map((benefit, index) => (
                      <li key={index}>
                        <span className="pricing-benefit-icon" aria-hidden="true">
                          ✓
                        </span>
                        <EditableText
                          path={`${sectionPath.pricing}.${
                            membershipPlan === 'monthly'
                              ? `monthlyBenefit${index + 1}`
                              : `fiveYearBenefit${index + 1}`
                          }`}
                          as="span"
                        >
                          {benefit}
                        </EditableText>
                      </li>
                    ))}
                  </ul>
                  <div className="pricing-summary membership-summary">
                    <span className="pricing-summary-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <path d="m7 7 10-3 4 4-3 10-10 3-4-4 3-10z" />
                        <path d="m10 10 4 4" />
                        <circle cx="9" cy="9" r="1" />
                      </svg>
                    </span>
                    <EditableText
                      path={`${sectionPath.pricing}.${
                        membershipPlan === 'monthly' ? 'monthlySummary1' : 'fiveYearSummary1'
                      }`}
                      as="span"
                      className="pricing-summary-item"
                    >
                      {membershipPlanContent.summaries[0]}
                    </EditableText>
                    <span className="pricing-summary-separator" aria-hidden="true" />
                    <EditableText
                      path={`${sectionPath.pricing}.${
                        membershipPlan === 'monthly' ? 'monthlySummary2' : 'fiveYearSummary2'
                      }`}
                      as="span"
                      className="pricing-summary-item"
                    >
                      {membershipPlanContent.summaries[1]}
                    </EditableText>
                  </div>
                  <EditableText
                    path={`${sectionPath.pricing}.membershipFootnote`}
                    as="p"
                    className="fine-print pricing-footnote"
                    multiline
                  >
                    {current.pricing.membershipFootnote}
                  </EditableText>
                </article>

                <article
                  className={`card pricing-showcase-card repair-card${
                    !editing && mobilePricingCard !== 'repair' ? ' is-mobile-hidden' : ''
                  }`}
                >
                  <EditableText path={`${sectionPath.pricing}.repairTitle`} as="h3">
                    {current.pricing.repairTitle}
                  </EditableText>
                  <div className="price">
                    <EditableText path={`${sectionPath.pricing}.repairAmount`} as="strong" richText={false}>
                      {current.pricing.repairAmount}
                    </EditableText>
                    <EditableText path={`${sectionPath.pricing}.repairPeriod`} as="span" richText={false}>
                      {current.pricing.repairPeriod}
                    </EditableText>
                  </div>
                  <EditableText path={`${sectionPath.pricing}.repairCopy`} as="p" className="pricing-copy" multiline>
                    {current.pricing.repairCopy}
                  </EditableText>
                  <ul className="list pricing-benefits">
                    {[
                      current.pricing.repairBenefit1,
                      current.pricing.repairBenefit2,
                      current.pricing.repairBenefit3,
                      current.pricing.repairBenefit4,
                    ].map((benefit, index) => (
                      <li key={index}>
                        <span className="pricing-benefit-icon" aria-hidden="true">
                          ✓
                        </span>
                        <EditableText path={`${sectionPath.pricing}.repairBenefit${index + 1}`} as="span">
                          {benefit}
                        </EditableText>
                      </li>
                    ))}
                  </ul>
                  <div className="pricing-summary repair-summary">
                    <span className="pricing-summary-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <path d="M12 3v18" />
                        <path d="M17 7.5c0-1.7-2.2-3-5-3s-5 1.3-5 3 2.2 3 5 3 5 1.3 5 3-2.2 3-5 3-5-1.3-5-3" />
                      </svg>
                    </span>
                    <EditableText path={`${sectionPath.pricing}.repairSummary1`} as="span" className="pricing-summary-item">
                      {current.pricing.repairSummary1}
                    </EditableText>
                    <span className="pricing-summary-separator" aria-hidden="true" />
                    <EditableText path={`${sectionPath.pricing}.repairSummary2`} as="span" className="pricing-summary-item">
                      {current.pricing.repairSummary2}
                    </EditableText>
                  </div>
                  <EditableText
                    path={`${sectionPath.pricing}.repairFootnote`}
                    as="p"
                    className="fine-print pricing-footnote"
                    multiline
                  >
                    {current.pricing.repairFootnote}
                  </EditableText>
                </article>
              </div>
            </div>
          </section>

          <section className="section" id={locale === 'pt' ? 'localizacao' : 'location'}>
            <div className="container">
              <div className="section-header services-header">
                <div className="section-kicker">
                  <EditableText path={`${sectionPath.location}.kicker`} as="span">
                    {current.location.kicker}
                  </EditableText>
                </div>
                <h2>
                  <EditableText path={`${sectionPath.location}.title`} as="span" richText={false}>
                    {current.location.title.replace(/\.$/, '')}
                  </EditableText>
                  <span className="services-dot">.</span>
                </h2>
                <SectionRule />
                <EditableText path={`${sectionPath.location}.body`} as="p" multiline>
                  {current.location.body}
                </EditableText>
              </div>

              <div className="location-grid">
                <article className="card contact-card">
                  <EditableText path={`${sectionPath.location}.cardTitle`} as="h3">
                    {current.location.cardTitle}
                  </EditableText>
                  <p>
                    <strong>
                      <EditableText path={`${sectionPath.location}.addressLabel`} as="span">
                        {current.location.addressLabel}
                      </EditableText>
                    </strong>
                    <br />
                    <a href={addressHref} onClick={handleTrackedClick('directions_click')}>
                      <EditableText path={`${sectionPath.location}.addressText`} as="span" multiline>
                        {current.location.addressText}
                      </EditableText>
                    </a>
                  </p>
                  <p>
                    <strong>
                      <EditableText path={`${sectionPath.location}.coordinatesLabel`} as="span">
                        {current.location.coordinatesLabel}
                      </EditableText>
                    </strong>
                    <br />
                    <a href={coordinatesHref} onClick={handleTrackedClick('directions_click')}>
                      <EditableText path={`${sectionPath.location}.coordinatesText`} as="span">
                        {current.location.coordinatesText}
                      </EditableText>
                    </a>
                  </p>
                  <EditableText path={`${sectionPath.location}.transitNote`} as="p" multiline>
                    {current.location.transitNote}
                  </EditableText>
                  <EditableText path={`${sectionPath.location}.emailLead`} as="p" multiline>
                    {current.location.emailLead}
                  </EditableText>
                  <p>
                    <a href={mailtoHref} onClick={(event) => preventEditNavigation(event, editing)}>
                      <EditableText path="brand.email" as="span" richText={false}>
                        {data.brand?.email ?? ''}
                      </EditableText>
                    </a>
                  </p>
                </article>

                <div
                  className="map-frame"
                  aria-label={
                    locale === 'pt'
                      ? 'Mapa com a localização do Car Keepers Club em Caniço, Madeira'
                      : 'Map showing Car Keepers Club location in Caniço, Madeira'
                  }
                >
                  <iframe
                    loading="lazy"
                    src="https://maps.google.com/maps?q=32.64832997700832%2C%20-16.8423094965472&t=m&z=15&output=embed&iwloc=near"
                    title={
                      locale === 'pt'
                        ? 'Localização do Car Keepers Club Madeira'
                        : 'Car Keepers Club Madeira location'
                    }
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="section faq-showcase" id={locale === 'pt' ? 'perguntas' : 'faq'}>
            <div className="container">
              <div className="faq-shell" data-faq-root>
                <div className="faq-head">
                  <div className="section-kicker">
                    {locale === 'pt' ? 'Perguntas comuns' : 'Common questions'}
                  </div>
                  <h2>
                    <EditableText path={`${sectionPath.faq}.title`} as="span" richText={false}>
                      {current.faq.title.replace(/\.$/, '')}
                    </EditableText>
                    <span className="faq-dot">.</span>
                  </h2>
                  <p>
                    {locale === 'pt'
                      ? 'Perguntas e respostas sobre adesão, preços, visitas, registos de serviço e funcionamento da Car Keepers Club na Madeira.'
                      : 'Questions and answers about membership, prices, visits, service records and how Car Keepers Club works in Madeira.'}
                  </p>
                </div>

                <div className="faq-search">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <circle cx="11" cy="11" r="6" />
                    <path d="m20 20-3.5-3.5" />
                  </svg>
                  <input
                    className="faq-search-input"
                    type="search"
                    placeholder={current.faq.searchPlaceholder}
                    aria-label={current.faq.searchPlaceholder}
                    value={faqQuery}
                    onChange={(event) => setFaqQuery(event.target.value)}
                    disabled={editing}
                  />
                </div>

                <div className="faq-chip-row">
                  {current.faq.chips.map((chipItem, index) => (
                    <button
                      className={`faq-chip${faqTopic === chipItem.topic ? ' is-active' : ''}`}
                      type="button"
                      key={`${locale}-chip-${index}`}
                      aria-pressed={faqTopic === chipItem.topic}
                      onClick={() => {
                        if (editing) return;
                        setFaqTopic(chipItem.topic);
                        setOpenFaqIndex(0);
                      }}
                      disabled={editing}
                    >
                      <FaqChipIcon topic={chipItem.topic} />
                      <EditableText path={`${sectionPath.faq}.chips.${index}.label`} as="span">
                        {chipItem.label}
                      </EditableText>
                    </button>
                  ))}
                </div>

                <div className="faq-layout">
                  <div className="faq-results">
                  {visibleFaqItems.map(({ item, originalIndex }, index) => (
                    <details
                      className="faq-item"
                      key={`${locale}-faq-${item.question}`}
                      open={editing || openFaqIndex === index}
                    >
                      <summary
                        onClick={(event) => {
                          if (editing) {
                            event.preventDefault();
                            return;
                          }

                          event.preventDefault();
                          setOpenFaqIndex((currentIndex) => (currentIndex === index ? -1 : index));
                        }}
                      >
                        <span className="faq-question">
                          <span className="faq-bullet">?</span>
                          <EditableText path={`${sectionPath.faq}.items.${originalIndex}.question`} as="span">
                            {item.question}
                          </EditableText>
                        </span>
                        <span className="faq-toggle" aria-hidden="true">
                          <svg viewBox="0 0 24 24">
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </span>
                      </summary>
                      <div className="answer">
                        <EditableText
                          path={`${sectionPath.faq}.items.${originalIndex}.answer`}
                          as="p"
                          multiline
                        >
                          {item.answer}
                        </EditableText>
                      </div>
                    </details>
                  ))}

                  {!editing && visibleFaqItems.length === 0 ? (
                    <div className="faq-empty">
                      <EditableText path={`${sectionPath.faq}.emptyState`} as="span" multiline>
                        {current.faq.emptyState}
                      </EditableText>
                    </div>
                  ) : null}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="cta">
            <div className="container">
              <div className="cta-panel">
                <div>
                  <div className="section-kicker">
                    <EditableText path={`${sectionPath.cta}.kicker`} as="span">
                      {current.cta.kicker}
                    </EditableText>
                  </div>
                  <EditableText path={`${sectionPath.cta}.title`} as="h2">
                    {current.cta.title}
                  </EditableText>
                  <EditableText path={`${sectionPath.cta}.body`} as="p" multiline>
                    {current.cta.body}
                  </EditableText>
                </div>
                <div className="cta-actions">
                  <a
                    className="button button-primary"
                    href={whatsappHref ?? '#'}
                    onClick={handleTrackedClick('whatsapp_click')}
                  >
                    <EditableText path={`${sectionPath.cta}.whatsappLabel`} richText={false}>
                      {current.cta.whatsappLabel}
                    </EditableText>
                  </a>
                  <a
                    className="button button-secondary"
                    href={mailtoHref}
                    onClick={(event) => preventEditNavigation(event, editing)}
                  >
                    <EditableText path={`${sectionPath.cta}.emailLabel`} richText={false}>
                      {current.cta.emailLabel}
                    </EditableText>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer>
          <div className="container footer-row">
            <EditableText path={`${sectionPath.footer}.copy`} as="span" richText={false}>
              {current.footer.copy}
            </EditableText>
            <nav className="footer-links" aria-label={locale === 'pt' ? 'Navegação do rodapé' : 'Footer navigation'}>
              <a href={privacyHref} target="_blank" rel="noopener" onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.footer}.privacyLabel`} richText={false}>
                  {current.footer.privacyLabel}
                </EditableText>
              </a>
              <a href={termsHref} target="_blank" rel="noopener" onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.footer}.termsLabel`} richText={false}>
                  {current.footer.termsLabel}
                </EditableText>
              </a>
              <a href={homeHref} onClick={(event) => preventEditNavigation(event, editing)}>
                <EditableText path={`${sectionPath.footer}.homeLabel`} richText={false}>
                  {current.footer.homeLabel}
                </EditableText>
              </a>
            </nav>
          </div>
        </footer>
      </div>
    </>
  );
}
